import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { createCourse, updateCourse, fetchCourseById, clearSelectedCourse } from '../../store/slices/courseSlice';
import { fetchCategories } from '../../store/slices/categorySlice';
import { fetchUniversities } from '../../store/slices/universitySlice';
import { showSuccess, showError } from '../../store/slices/notificationSlice';
import { Card, Button, Input, Select, FileUpload } from '../../components/Common';
import { useOptions } from '../../hooks/useOptions';

const CourseForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { selectedCourse } = useSelector((state) => state.course);
  const { categories } = useSelector((state) => state.category);
  const { universities } = useSelector((state) => state.university);

  const { getSelectOptions: getLevelOptions } = useOptions('courseLevel');
  const { getSelectOptions: getStatusOptions } = useOptions('courseStatus');

  const isEdit = Boolean(id);
  const [submitting, setSubmitting] = useState(false);
  const [thumbnail, setThumbnail] = useState(null);
  const [formData, setFormData] = useState({
    title: '', description: '', instructor: '', duration: '',
    level: 'beginner', price: 0, category_id: '', university_id: '', status: 'draft'
  });

  useEffect(() => {
    dispatch(fetchCategories({ size: 100 }));
    dispatch(fetchUniversities({ size: 100 }));
    if (isEdit) {
      dispatch(fetchCourseById({ id, withRelationships: true }));
    }
    return () => { dispatch(clearSelectedCourse()); };
  }, [dispatch, id, isEdit]);

  useEffect(() => {
    if (isEdit && selectedCourse) {
      setFormData({
        title: selectedCourse.title || '',
        description: selectedCourse.description || '',
        instructor: selectedCourse.instructor || '',
        duration: selectedCourse.duration || '',
        level: selectedCourse.level || 'beginner',
        price: selectedCourse.price || 0,
        category_id: selectedCourse.category_id?.toString() || '',
        university_id: selectedCourse.university_id?.toString() || '',
        status: selectedCourse.status || 'draft'
      });
    }
  }, [isEdit, selectedCourse]);

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      dispatch(showError('Course title is required'));
      return;
    }

    setSubmitting(true);
    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== undefined && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });
      if (thumbnail) submitData.append('image', thumbnail);

      if (isEdit) {
        await dispatch(updateCourse({ id, data: submitData })).unwrap();
        dispatch(showSuccess('Course updated successfully'));
      } else {
        await dispatch(createCourse(submitData)).unwrap();
        dispatch(showSuccess('Course created successfully'));
      }
      navigate('/courses');
    } catch (error) {
      const errorMessage = typeof error === 'string' ? error :
        error?.message || error?.error || `Failed to ${isEdit ? 'update' : 'create'} course`;
      dispatch(showError(errorMessage));
    } finally {
      setSubmitting(false);
    }
  };

  const levelOptions = getLevelOptions();
  const statusOptions = getStatusOptions();
  const categoryOptions = categories.map(cat => ({ value: cat.id.toString(), label: cat.name }));
  const universityOptions = universities.map(uni => ({ value: uni.id.toString(), label: uni.name }));

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/courses')} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
          <ArrowLeftIcon className="w-5 h-5 text-slate-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{isEdit ? 'Edit Course' : 'New Course'}</h1>
          <p className="text-slate-500">{isEdit ? 'Update course information' : 'Create a new educational course'}</p>
        </div>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input label="Title *" value={formData.title} onChange={handleChange('title')} placeholder="Course title" required />
            <Input label="Instructor" value={formData.instructor} onChange={handleChange('instructor')} placeholder="Instructor name" />
          </div>

          <Input label="Description" value={formData.description} onChange={handleChange('description')} placeholder="Short description" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Input label="Duration" value={formData.duration} onChange={handleChange('duration')} placeholder="e.g., 10 hours" />
            <Select label="Level" value={formData.level} onChange={handleChange('level')} options={levelOptions} />
            <Input label="Price" type="number" value={formData.price} onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))} placeholder="0" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Select label="Category" value={formData.category_id} onChange={handleChange('category_id')} options={categoryOptions} placeholder="Select category" />
            <Select label="University" value={formData.university_id} onChange={handleChange('university_id')} options={universityOptions} placeholder="Select university" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Select label="Status" value={formData.status} onChange={handleChange('status')} options={statusOptions} />
            <FileUpload label="Thumbnail" accept="image/*" maxSize={5} onChange={setThumbnail} preview={selectedCourse?.thumbnail_url} />
          </div>

          <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
            <Button type="submit" loading={submitting}>{isEdit ? 'Update Course' : 'Create Course'}</Button>
            <Button variant="secondary" onClick={() => navigate('/courses')}>Cancel</Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default CourseForm;